import { TemplateTypes } from '../../../../../../../lib/models/TemplateTypes';
import { PrefillFields, ProductKind } from '../../../../../../../lib/models/Questionnaire';
import { isIndexFound, removeProductsDuplicates } from '../../../../../../../lib/util/helpers';
import { Product } from '../../FindersAndProductsPage/lib/Product';
import { getProductStatus, normalizeExternalId, toInteger } from './prefill-status';
import { applyAnswerPrefill } from './answer-prefill';

const NUMERIC_ANSWER_FIELDS = ['counterAnswers', 'chartAnswers'];

const CUSTOMER_CODE_MAX_LENGTH = 16;

function shouldIncludeProductInChapterResult(product) {
  if (product?.productNameSection?.product?.title === 'Keine girocard') {
    return true;
  }

  return Boolean(
    product?.selected ||
      (product?.counterSection && product?.counterSection?.value > 0) ||
      (product?.counterSection?.ruleCounter && product?.counterSection?.ruleCounter > 0)
  );
}

function getChapterPageIds(chapter) {
  const chapterPageIds = (chapter?.chapterPages || []).map(page => page.id);

  return new Set([...chapterPageIds, ...(chapter?.chapterInnerPagesIds || [])].filter(Boolean));
}

function getSelectedProductsForChapter(chapter, allPages) {
  const chapterPageIds = getChapterPageIds(chapter);
  const selectedProducts = allPages
    .filter(page => chapterPageIds.has(page.id))
    .flatMap(page => page.elements.filter(element => element.templateType === TemplateTypes.PRODUCT))
    .flatMap(element => element?.content?.products || [])
    .filter(shouldIncludeProductInChapterResult);

  return removeProductsDuplicates(selectedProducts);
}

export function sanitizeCustomerCode(value) {
  return value?.trim?.() || '';
}

export function isCustomerCodeWithinLimit(value) {
  return sanitizeCustomerCode(value).length <= CUSTOMER_CODE_MAX_LENGTH;
}

/**
 * The backend answers configuration problems (an invalid or unreachable prefilled products URL,
 * for example) with an explicit message. Show it as is, and keep the generic wording for the
 * failures that carry no message.
 */
export function getPrefillErrorMessage(error, fallbackMessage) {
  const backendMessage = error?.response?.data?.message;

  return typeof backendMessage === 'string' && backendMessage.trim() ? backendMessage.trim() : fallbackMessage;
}

/**
 * The prefill link handed to the client systems carries the customer code as `?code=...`.
 * HVM prefills its pseudo code from the same parameter name, so both share this reader.
 */
export const CUSTOMER_CODE_URL_PARAM = 'code';

export function readCustomerCodeFromUrl(search) {
  const rawValue = new URLSearchParams(search || '').get(CUSTOMER_CODE_URL_PARAM);

  return sanitizeCustomerCode(rawValue);
}

/**
 * The finder is resolved by the current URL, so the customer code has to be out of it before the
 * lookup; it has no place in the browser history or in a referrer either, and the same cleaned
 * URL is what replaces the address once the code is read.
 */
export function getUrlWithoutCustomerCode(href, locationObject = window.location) {
  try {
    const url = new URL(href, locationObject.origin);

    url.searchParams.delete(CUSTOMER_CODE_URL_PARAM);

    return url.toString();
  } catch {
    return href;
  }
}

let capturedCustomerCode = null;

/**
 * The address can be rewritten before the finder mounts: while the tenant domains are still
 * loading the app renders the auth routes, whose catch-all navigates to the log-in page and drops
 * the query along the way. So the code is taken out of the URL once, at startup, and kept here.
 */
export function captureCustomerCodeFromUrl(locationObject = window.location, historyObject = window.history) {
  capturedCustomerCode = readCustomerCodeFromUrl(locationObject.search);

  if (capturedCustomerCode) {
    historyObject.replaceState(historyObject.state, '', getUrlWithoutCustomerCode(locationObject.href, locationObject));
  }

  return capturedCustomerCode;
}

/**
 * Falls back to the current address for the renders that happen without the startup capture, a
 * mounted-in-isolation component in a test, for one.
 */
export function getCapturedCustomerCode() {
  return capturedCustomerCode === null ? readCustomerCodeFromUrl(window.location.search) : capturedCustomerCode;
}

export function getFinderUrlForPrefill({ finderUrl, locationObject = window.location } = {}) {
  if (finderUrl) {
    return finderUrl;
  }

  return `${locationObject.origin}${locationObject.pathname}`;
}

function getNormalizedProductStatuses(productStatuses) {
  if (!productStatuses || typeof productStatuses !== 'object') {
    return {};
  }

  return Object.fromEntries(
    Object.entries(productStatuses)
      .map(([key, value]) => [normalizeExternalId(key), value])
      .filter(([key]) => key)
  );
}

function clampToRange(value, min, max) {
  const lowerBound = Number.isInteger(min) ? min : value;
  const upperBound = Number.isInteger(max) ? max : value;

  return Math.min(Math.max(value, lowerBound), upperBound);
}

/**
 * The status doubles as an amount only for products with an enabled counter (e.g. Genossenschaftsanteile).
 * Every product carries a counterSection, so without the showCounter check a status of 3 would
 * multiply the price and the result cards of an ordinary product the customer simply owns.
 */
function getCounterSectionWithStatus(counterSection, status) {
  if (!counterSection?.showCounter) {
    return counterSection;
  }

  const min = Number.isInteger(counterSection.min) ? counterSection.min : 1;

  return {
    ...counterSection,
    value: clampToRange(status, min, counterSection.max),
  };
}

/**
 * A numeric or chart answer may carry several external ids (e.g. the booking question splits into
 * beleglos / online / terminal), so the prefilled amount is their sum. Ids missing from the response
 * are skipped; if none of them is present the answer keeps its configured amount.
 */
function getPrefilledAmount(productStatuses, externalPrefillIds) {
  if (!Array.isArray(externalPrefillIds)) {
    return null;
  }

  let amount = null;

  for (const externalPrefillId of externalPrefillIds) {
    const value = toInteger(productStatuses?.[normalizeExternalId(externalPrefillId)]);

    if (value !== null) {
      amount = (amount ?? 0) + value;
    }
  }

  return amount;
}

/**
 * One counter serves the whole one-counter block and shows the selected product's value, so there the
 * counter is on for every product but the exceptional ones (e.g. "Keine girocard"). A multiple-counters
 * product has its own switch, and a code left behind on a switched-off counter must not multiply its price.
 */
function isCounterEnabled(product, productKind) {
  if (productKind === ProductKind.ONE_COUNTER) {
    return !product?.[Product.IS_EXCEPTIONAL];
  }

  return product?.counterSection?.showCounter === true;
}

/**
 * The counter has its own code, separate from the ownership one, since the response carries only a code
 * and a number: one code says "has a credit card", another one "has 3 of them". A missing code returns
 * null and leaves the product to the ownership code, while an explicit 0 still counts as an answer.
 */
function getCounterPrefillValue(product, productKind, productStatuses) {
  if (!isCounterEnabled(product, productKind)) {
    return null;
  }

  const value = getPrefilledAmount(productStatuses, product?.counterSection?.[PrefillFields.COUNTER_PREFILL_IDS]);

  return value === null ? null : Math.max(value, 0);
}

/**
 * The counter value wins over the ownership code and is shown as is, without the min/max clamp. A value
 * selects the product even without an ownership code; a 0 leaves the selection as configured.
 */
function getProductWithCounterPrefill(product, counterValue) {
  const counterSection = { ...product?.counterSection, value: counterValue };

  if (counterValue === 0) {
    return { ...product, counterSection };
  }

  return { ...product, selected: true, prefilled: true, counterSection };
}

function getProductWithOwnershipPrefill(product, productStatuses) {
  const externalId = product?.externalId;
  const status = getProductStatus(productStatuses, externalId);

  if (!externalId || status === null) {
    return product;
  }

  return {
    ...product,
    selected: true,
    prefilled: true,
    counterSection: getCounterSectionWithStatus(product?.counterSection, status),
  };
}

/**
 * A one-counter block selects a single product, while the ownership and counter codes may point to
 * different ones. The first product with a counter value keeps the selection, the rest stay as configured.
 */
function getPrefilledProducts(content, productStatuses) {
  const counterValues = content.products.map(product => getCounterPrefillValue(product, content.kind, productStatuses));
  const prefilledProducts = content.products.map((product, index) =>
    counterValues[index] === null
      ? getProductWithOwnershipPrefill(product, productStatuses)
      : getProductWithCounterPrefill(product, counterValues[index])
  );

  const counterProductIndex = counterValues.findIndex(value => value > 0);

  if (content.kind !== ProductKind.ONE_COUNTER || !isIndexFound(counterProductIndex)) {
    return prefilledProducts;
  }

  return prefilledProducts.map((product, index) => (index === counterProductIndex ? product : content.products[index]));
}

function getAnswersWithPrefilledAmount(answers, productStatuses) {
  if (!Array.isArray(answers)) {
    return answers;
  }

  return answers.map(answer => {
    const amount = getPrefilledAmount(productStatuses, answer?.[PrefillFields.EXTERNAL_PREFILL_IDS]);

    if (amount === null) {
      return answer;
    }

    return {
      ...answer,
      amount: clampToRange(amount, answer?.minValue, answer?.maxValue),
    };
  });
}

function getQuestionWithPrefilledAnswers(element, productStatuses) {
  const prefilledFields = NUMERIC_ANSWER_FIELDS.filter(field => Array.isArray(element?.content?.[field]));

  if (prefilledFields.length === 0) {
    return element;
  }

  const updatedContent = { ...element.content };

  for (const field of prefilledFields) {
    updatedContent[field] = getAnswersWithPrefilledAmount(element.content[field], productStatuses);
  }

  return { ...element, content: updatedContent };
}

export function getProductPrefillSource({ allPages = [], allChapters = [], productPrefillBaseline } = {}) {
  return {
    allPages: productPrefillBaseline?.allPages ?? allPages,
    allChapters: productPrefillBaseline?.allChapters ?? allChapters,
  };
}

function getFinderWithDefaultVariables(finder) {
  if (!finder || !Array.isArray(finder.customVariables)) {
    return finder;
  }

  return {
    ...finder,
    customVariables: finder.customVariables.map(variable => ({
      ...variable,
      value: variable.defaultValue,
    })),
  };
}

/**
 * Starting another customer lookup creates a new finder session. Besides restoring the pages,
 * clear every derived value that can otherwise leak scoring, rules or result state from the
 * previous customer. Navigation location itself is preserved until the intro button continues.
 */
export function getCustomerPrefillRuntimeReset({ productPrefillBaseline, prefillSessionVersion = 0, finder } = {}) {
  return {
    answersScoring: productPrefillBaseline?.answersScoring ?? {},
    progressPercentage: 0,
    rulesProductsProperties: [],
    rulesAnswersScore: [],
    rulesAnswersSavings: [],
    clickedButtonsIds: [],
    hideProducts: [],
    prevPageIds: [],
    completed: false,
    resultTableContent: null,
    showContactForm: false,
    showSuccessScreen: false,
    afterContactFormLink: null,
    isFormHardcoded: false,
    isDynamicFormSubmitting: false,
    pdfId: null,
    pdfLink: null,
    stringifiedPrintedResultProps: null,
    prefillSessionVersion: prefillSessionVersion + 1,
    ...(finder ? { finder: getFinderWithDefaultVariables(finder) } : {}),
  };
}

/**
 * Applies one customer lookup from the baseline: owned products and numeric answers first, then the prefilled
 * answers, which may reset products of the branches they close. The chapter results are counted last,
 * from the final pages, so a product reset by an answer does not stay in a chapter result.
 */
export function applyProductPrefillToPagesFlow({
  allPages = [],
  allChapters = [],
  productStatuses: rawProductStatuses = {},
  productPrefillBaseline,
}) {
  const productStatuses = getNormalizedProductStatuses(rawProductStatuses);
  const prefillSource = getProductPrefillSource({ allPages, allChapters, productPrefillBaseline });
  const pagesWithProductPrefill = prefillSource.allPages.map(page => ({
    ...page,
    elements: page.elements.map(element => {
      if (element.templateType === TemplateTypes.QUESTION) {
        return getQuestionWithPrefilledAnswers(element, productStatuses);
      }

      if (element.templateType !== TemplateTypes.PRODUCT || !Array.isArray(element?.content?.products)) {
        return element;
      }

      return {
        ...element,
        content: {
          ...element.content,
          products: getPrefilledProducts(element.content, productStatuses),
        },
      };
    }),
  }));

  const { allPages: updatedPages, answersScoring } = applyAnswerPrefill({
    allPages: pagesWithProductPrefill,
    answersScoring: productPrefillBaseline?.answersScoring ?? {},
    productStatuses,
  });

  const updatedChapters = prefillSource.allChapters.map(chapter => ({
    ...chapter,
    resultSelectedProducts: getSelectedProductsForChapter(chapter, updatedPages),
  }));

  return {
    allPages: updatedPages,
    allChapters: updatedChapters,
    answersScoring,
  };
}
